import { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import type {
    Broadcast,
    BroadcastAudience,
    BroadcastStatus,
    EmailTemplate as EmailTemplateT,
} from '@/types/communications'
import { TemplatePicker } from './TemplatePicker'
import { SlotValueForm } from './SlotValueForm'
import { PreviewPane } from './PreviewPane'
import { AudiencePicker } from './AudiencePicker'
import {
    Badge,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    ConfirmDialog,
    EmptyState,
    Field,
    Input,
    LoadingPanel,
    PillButton,
    SectionTitle,
} from './_shared'
import { RichTextEditor } from '../../components/editor/RichTextEditor'
import {
    Megaphone,
    ChevronLeft,
    Send,
    Plus,
    Eye,
    FileText,
    Code,
    Pencil,
} from 'lucide-react'

// Use user.*-prefixed keys so the "Insert variable" menu produces markers
// the renderer auto-binds per-recipient. Bare names like `{{firstName}}` are
// treated as static admin-filled values and will not personalize.
const BROADCAST_VARIABLES = [
    { key: 'user.firstName', label: 'First name' },
    { key: 'user.name', label: 'Full name' },
    { key: 'user.email', label: 'Email' },
    { key: 'user.orgName', label: 'Organization name' },
]

const STATUS_VARIANT: Record<
    BroadcastStatus,
    'default' | 'success' | 'warning' | 'error' | 'info'
> = {
    DRAFT: 'default',
    SCHEDULED: 'info',
    SENDING: 'warning',
    SENT: 'success',
    FAILED: 'error',
    CANCELLED: 'default',
}

export function BroadcastsTab() {
    const [items, setItems] = useState<Broadcast[]>([])
    const [loading, setLoading] = useState(true)
    const [composing, setComposing] = useState<boolean | Broadcast>(false)

    const load = async () => {
        setLoading(true)
        try {
            const res = await adminApi.getBroadcasts()
            setItems(res.items)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, [])

    if (composing) {
        return (
            <BroadcastComposer
                existing={typeof composing === 'object' ? composing : null}
                onCancel={() => setComposing(false)}
                onCreated={async () => {
                    await load()
                    setComposing(false)
                }}
            />
        )
    }

    return (
        <div className="space-y-6">
            <SectionTitle
                title="Broadcasts"
                description="One-off announcements sent to a chosen audience. Each send creates a per-user delivery row in the log."
                actions={
                    <PillButton variant="primary" onClick={() => setComposing(true)}>
                        <Plus className="h-3.5 w-3.5" /> New broadcast
                    </PillButton>
                }
            />

            {loading ? (
                <LoadingPanel label="Loading broadcasts…" />
            ) : items.length === 0 ? (
                <EmptyState
                    icon={Megaphone}
                    title="No broadcasts yet"
                    description="Use broadcasts for product updates, maintenance notices, and announcements that don't fit a lifecycle campaign."
                    action={
                        <PillButton variant="primary" onClick={() => setComposing(true)}>
                            <Plus className="h-3.5 w-3.5" /> Compose your first broadcast
                        </PillButton>
                    }
                />
            ) : (
                <div className="space-y-3">
                    {items.map(b => (
                        <BroadcastRow
                            key={b.id}
                            broadcast={b}
                            onSent={load}
                            onEdit={() => setComposing(b)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

function BroadcastRow({
    broadcast,
    onSent,
    onEdit,
}: {
    broadcast: Broadcast
    onSent: () => void | Promise<void>
    onEdit: () => void
}) {
    const [sending, setSending] = useState(false)
    const [confirmSend, setConfirmSend] = useState(false)
    const handleSend = async () => {
        setSending(true)
        try {
            await adminApi.sendBroadcast(broadcast.id)
            await onSent()
        } finally {
            setSending(false)
            setConfirmSend(false)
        }
    }
    return (
        <Card>
            <CardContent>
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center gap-2">
                            <span className="text-xs uppercase tracking-wider text-gray-500">
                                {broadcast.audience.replace(/_/g, ' ').toLowerCase()}
                            </span>
                            <Badge variant={STATUS_VARIANT[broadcast.status]}>
                                {broadcast.status.toLowerCase()}
                            </Badge>
                        </div>
                        <div className="text-sm font-semibold text-gray-900">
                            {broadcast.title}
                        </div>
                        <div className="mt-0.5 truncate text-xs text-gray-600">
                            {broadcast.subject}
                        </div>
                    </div>
                    <div className="shrink-0 text-right text-xs tabular-nums text-gray-500">
                        <div>
                            <span className="font-semibold text-gray-900">
                                {broadcast.sentCount.toLocaleString()}
                            </span>
                            <span> / </span>
                            <span>{broadcast.recipientCount.toLocaleString()}</span>
                        </div>
                        <div className="text-[10px] text-gray-400">
                            {broadcast.failedCount > 0 && (
                                <span className="text-red-600">
                                    {broadcast.failedCount} failed ·{' '}
                                </span>
                            )}
                            {new Date(broadcast.createdAt).toLocaleString(undefined, {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                            })}
                        </div>
                    </div>
                    {(broadcast.status === 'DRAFT' ||
                        broadcast.status === 'SCHEDULED') && (
                        <div className="flex shrink-0 items-center gap-2">
                            <PillButton onClick={onEdit} disabled={sending}>
                                <Pencil className="h-3.5 w-3.5" /> Edit
                            </PillButton>
                            <PillButton
                                variant="primary"
                                onClick={() => setConfirmSend(true)}
                                disabled={sending}
                            >
                                <Send className="h-3.5 w-3.5" />
                                {sending ? 'Sending…' : 'Send now'}
                            </PillButton>
                        </div>
                    )}
                </div>
                {broadcast.error && (
                    <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 font-mono text-xs text-red-900">
                        {broadcast.error}
                    </div>
                )}
            </CardContent>
            <ConfirmDialog
                open={confirmSend}
                onCancel={() => setConfirmSend(false)}
                onConfirm={handleSend}
                title="Send broadcast?"
                message={`This will send "${broadcast.title}" to ${broadcast.recipientCount.toLocaleString()} recipient(s). This cannot be undone.`}
                confirmLabel="Send"
                confirmVariant="primary"
                loading={sending}
            />
        </Card>
    )
}

function BroadcastComposer({
    existing,
    onCancel,
    onCreated,
}: {
    existing: Broadcast | null
    onCancel: () => void
    onCreated: () => void | Promise<void>
}) {
    const isEditing = !!existing
    const existingFilter = (existing?.audienceFilter ?? {}) as {
        organizationIds?: string[]
        feature?: string
        operator?: 'gt' | 'gte' | 'lt' | 'lte' | 'eq'
        threshold?: number
        userIds?: string[]
        emails?: string[]
    }
    const [title, setTitle] = useState(existing?.title ?? '')
    const [subject, setSubject] = useState(existing?.templateId ? '' : (existing?.subject ?? ''))
    const [bodyHtml, setBodyHtml] = useState(existing?.templateId ? '' : (existing?.bodyHtml ?? ''))
    const [bodyText, setBodyText] = useState(existing?.bodyText ?? '')
    const [audience, setAudience] = useState<BroadcastAudience>(existing?.audience ?? 'ALL_USERS')
    const [orgIds, setOrgIds] = useState(existingFilter.organizationIds?.join(', ') ?? '')
    const [feature, setFeature] = useState(existingFilter.feature ?? '')
    const [featureOperator, setFeatureOperator] = useState<
        'gt' | 'gte' | 'lt' | 'lte' | 'eq'
    >(existingFilter.operator ?? 'gte')
    const [featureThreshold, setFeatureThreshold] = useState<number | null>(
        typeof existingFilter.threshold === 'number' ? existingFilter.threshold : null,
    )
    const [userList, setUserList] = useState(
        [
            ...(existingFilter.emails ?? []),
            ...(existingFilter.userIds ?? []),
        ].join('\n'),
    )
    const [experimentKey, setExperimentKey] = useState(existing?.experimentKey ?? '')
    const [view, setView] = useState<'editor' | 'preview' | 'html'>('editor')
    const [busy, setBusy] = useState(false)

    // Template mode
    const [useTemplate, setUseTemplate] = useState(!!existing?.templateId)
    const [template, setTemplate] = useState<EmailTemplateT | null>(null)
    const [slotValues, setSlotValues] = useState<Record<string, unknown>>(
        (existing?.slotValues as Record<string, unknown> | null) ?? {},
    )
    const [templatePreview, setTemplatePreview] = useState('')
    const [contentPrompt, setContentPrompt] = useState('')
    const [aiBusy, setAiBusy] = useState(false)

    // Hydrate the pinned template when editing
    useEffect(() => {
        if (!existing?.templateId) return
        let cancelled = false
        ;(async () => {
            try {
                const tpls = await adminApi.getEmailTemplates()
                if (cancelled) return
                const match = tpls.items.find(t => t.id === existing.templateId)
                if (match) setTemplate(match)
            } catch {
                /* ignore — user can re-pick */
            }
        })()
        return () => {
            cancelled = true
        }
    }, [existing?.templateId])

    useEffect(() => {
        if (!useTemplate || !template) return
        const t = setTimeout(async () => {
            try {
                const r = await adminApi.previewTemplate(template.id, slotValues)
                setTemplatePreview(r.html)
            } catch (e) {
                setTemplatePreview(
                    `<pre style="color:#C2410C;padding:32px;">${(e as Error).message}</pre>`,
                )
            }
        }, 300)
        return () => clearTimeout(t)
    }, [useTemplate, template, slotValues])

    async function aiFill() {
        if (!template || !contentPrompt.trim()) return
        setAiBusy(true)
        try {
            const r = await adminApi.aiGenerateContent(template.id, contentPrompt)
            setSlotValues(r.slotValues)
        } finally {
            setAiBusy(false)
        }
    }

    const handleSend = async (sendNow: boolean) => {
        setBusy(true)
        try {
            const audienceFilter: Record<string, unknown> = {}
            if (audience === 'BY_ORG') {
                audienceFilter.organizationIds = orgIds
                    .split(/[,\s]+/)
                    .map(s => s.trim())
                    .filter(Boolean)
            } else if (audience === 'BY_FEATURE_USAGE') {
                audienceFilter.feature = feature.trim()
                if (typeof featureThreshold === 'number') {
                    audienceFilter.operator = featureOperator
                    audienceFilter.threshold = featureThreshold
                }
            } else if (audience === 'BY_USERS') {
                const tokens = userList
                    .split(/[\s,]+/)
                    .map(s => s.trim())
                    .filter(Boolean)
                const emails = tokens.filter(t => t.includes('@'))
                const userIds = tokens.filter(t => !t.includes('@'))
                audienceFilter.emails = emails
                audienceFilter.userIds = userIds
            }
            if (isEditing && existing) {
                await adminApi.updateBroadcast(existing.id, {
                    title,
                    subject: useTemplate ? undefined : subject,
                    bodyHtml: useTemplate ? undefined : bodyHtml,
                    bodyText: bodyText || undefined,
                    audience,
                    audienceFilter,
                    experimentKey: experimentKey.trim() || null,
                    templateId: useTemplate ? template?.id ?? null : null,
                    slotValues: useTemplate ? slotValues : null,
                })
                if (sendNow) {
                    await adminApi.sendBroadcast(existing.id)
                }
            } else {
                await adminApi.createBroadcast({
                    title,
                    subject: useTemplate ? undefined : subject,
                    bodyHtml: useTemplate ? undefined : bodyHtml,
                    bodyText: bodyText || undefined,
                    audience,
                    audienceFilter,
                    sendNow,
                    experimentKey: experimentKey.trim() || undefined,
                    templateId: useTemplate ? template?.id ?? null : null,
                    slotValues: useTemplate ? slotValues : null,
                })
            }
            await onCreated()
        } finally {
            setBusy(false)
        }
    }

    const baseValid = useTemplate
        ? !!(title && template)
        : !!(title && subject && bodyHtml)
    const audienceValid =
        audience === 'BY_USERS'
            ? userList.split(/[\s,]+/).some(s => s.trim().length > 0)
            : audience === 'BY_ORG'
              ? orgIds.split(/[\s,]+/).some(s => s.trim().length > 0)
              : audience === 'BY_FEATURE_USAGE'
                ? feature.trim().length > 0
                : true
    const valid = baseValid && audienceValid

    return (
        <div className="space-y-6">
            <button
                onClick={onCancel}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to broadcasts
            </button>

            <SectionTitle
                title={isEditing ? 'Edit broadcast' : 'Compose broadcast'}
                description={
                    isEditing
                        ? 'Editing allowed while status is DRAFT or SCHEDULED. Save keeps the current status; Send now fires immediately.'
                        : "Drafts can be saved and sent later. 'Send now' fires immediately to the selected audience."
                }
                actions={
                    <>
                        <PillButton onClick={() => handleSend(false)} disabled={!valid || busy}>
                            {isEditing ? (busy ? 'Saving…' : 'Save changes') : 'Save as draft'}
                        </PillButton>
                        <PillButton
                            variant="primary"
                            onClick={() => handleSend(true)}
                            disabled={!valid || busy}
                        >
                            <Send className="h-3.5 w-3.5" /> {busy ? 'Sending…' : 'Send now'}
                        </PillButton>
                    </>
                }
            />

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="space-y-4 lg:col-span-2">
                    <Card>
                        <CardContent>
                            <Field
                                label="Internal title"
                                hint="Used in the broadcast list — never shown to recipients."
                                required
                            >
                                <Input
                                    value={title}
                                    onChange={e => setTitle(e.target.value)}
                                    placeholder="e.g. April product update"
                                />
                            </Field>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent>
                            <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={useTemplate}
                                    onChange={e => setUseTemplate(e.target.checked)}
                                />
                                Use a template
                            </label>
                            {useTemplate && (
                                <div className="space-y-4">
                                    <TemplatePicker
                                        category="BROADCAST"
                                        value={template?.id ?? null}
                                        onChange={(_, t) => {
                                            setTemplate(t)
                                            setSlotValues(
                                                (t?.previewValues as Record<string, unknown> | null) ?? {},
                                            )
                                        }}
                                    />
                                    {template && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-3">
                                                <Field label="Describe the content (for AI fill)">
                                                    <textarea
                                                        value={contentPrompt}
                                                        onChange={e => setContentPrompt(e.target.value)}
                                                        className="block h-24 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                                                        placeholder="e.g. Announce our new Slack connector with three concrete workflow examples."
                                                    />
                                                </Field>
                                                <SlotValueForm
                                                    schema={template.variables ?? []}
                                                    values={slotValues}
                                                    onChange={setSlotValues}
                                                    onAIFill={aiFill}
                                                    aiBusy={aiBusy}
                                                />
                                            </div>
                                            <PreviewPane
                                                html={templatePreview}
                                                heightClass="h-[500px]"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {!useTemplate && (
                        <Card>
                            <CardContent>
                                <Field label="Email subject" required>
                                    <Input
                                        value={subject}
                                        onChange={e => setSubject(e.target.value)}
                                        placeholder="What's new in Libra"
                                    />
                                </Field>
                            </CardContent>
                        </Card>
                    )}

                    {!useTemplate && (
                        <>
                            <Card>
                                <CardHeader className="!py-2">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="!text-sm">Body</CardTitle>
                                        <div className="flex items-center gap-1">
                                            <ViewBtn
                                                active={view === 'editor'}
                                                onClick={() => setView('editor')}
                                            >
                                                <FileText className="h-3.5 w-3.5" /> Editor
                                            </ViewBtn>
                                            <ViewBtn
                                                active={view === 'preview'}
                                                onClick={() => setView('preview')}
                                            >
                                                <Eye className="h-3.5 w-3.5" /> Preview
                                            </ViewBtn>
                                            <ViewBtn
                                                active={view === 'html'}
                                                onClick={() => setView('html')}
                                            >
                                                <Code className="h-3.5 w-3.5" /> HTML
                                            </ViewBtn>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="!p-0">
                                    {view === 'editor' && (
                                        <div className="p-4">
                                            <RichTextEditor
                                                value={bodyHtml}
                                                onChange={setBodyHtml}
                                                variables={BROADCAST_VARIABLES}
                                                placeholder="Write your announcement…"
                                                minHeight={340}
                                            />
                                        </div>
                                    )}
                                    {view === 'preview' && (
                                        <div className="bg-gray-50 p-4">
                                            <iframe
                                                srcDoc={
                                                    bodyHtml ||
                                                    "<p style='color:#9CA3AF;font-family:sans-serif;padding:24px'>No content yet</p>"
                                                }
                                                className="min-h-[400px] w-full rounded-md border border-gray-200 bg-white"
                                                title="Broadcast preview"
                                                sandbox=""
                                            />
                                        </div>
                                    )}
                                    {view === 'html' && (
                                        <textarea
                                            value={bodyHtml}
                                            onChange={e => setBodyHtml(e.target.value)}
                                            placeholder="<div>...</div>"
                                            rows={18}
                                            className="w-full resize-y border-0 p-4 font-mono text-xs text-gray-900 outline-none"
                                        />
                                    )}
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent>
                                    <Field label="Plain text fallback (optional)">
                                        <textarea
                                            value={bodyText}
                                            onChange={e => setBodyText(e.target.value)}
                                            rows={4}
                                            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                    </Field>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>

                <div className="space-y-4">
                    <AudiencePicker
                        audience={audience}
                        onAudienceChange={setAudience}
                        orgIds={orgIds}
                        onOrgIdsChange={setOrgIds}
                        feature={feature}
                        onFeatureChange={setFeature}
                        featureOperator={featureOperator}
                        onFeatureOperatorChange={setFeatureOperator}
                        featureThreshold={featureThreshold}
                        onFeatureThresholdChange={setFeatureThreshold}
                        userList={userList}
                        onUserListChange={setUserList}
                    />

                    <Card>
                        <CardContent>
                            <Field
                                label="Experiment key (optional)"
                                hint="Reference an experiment to A/B test subject + body across recipients. Leave blank to send the same content to everyone."
                            >
                                <Input
                                    value={experimentKey}
                                    onChange={e => setExperimentKey(e.target.value)}
                                    placeholder="e.g. welcome_subject_v1"
                                />
                            </Field>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    )
}

function ViewBtn({
    active,
    onClick,
    children,
}: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                active
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
            }`}
        >
            {children}
        </button>
    )
}
